import type { Ref } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'

import { DisciplineFilesModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineFilesStore } from './files/store'
import { useDisciplineFilesApi } from '../api/files.hook'
import { useDisciplineFilePackDeleteApi } from '../api/file-pack.hook'
import { useDisciplineCertificateApi } from '../api/certificate.hook'
import { getDisciplineName } from './discipline-general-info'
import { getTaskOptionText } from './discipline-files'

interface ModalDisciplineFilesConfirmReturn {
  refIsDeleteOpen: ComputedRef<boolean>
  refIsCertificateOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refCertificateTitle: ComputedRef<string>
  refTaskOptions: ComputedRef<NativeSelectOption[]>
  refComment: Ref<string>
  setTask: (option: NativeSelectOption) => void
  setComment: (event: AppTextareaEvent) => void
  closeModal: () => void
  deletePack: () => Promise<void>
  generateCertificate: () => Promise<void>
}

/** The pack delete confirmation and the certificate dialog — both one request, then a refetch. */
export const useModalDisciplineFilesConfirm = (): ModalDisciplineFilesConfirmReturn => {
  const route = useRoute()
  const disciplineStore = useDisciplineStore()
  const store = useDisciplineFilesStore()
  const listsStore = useListsStore()

  const refIsLoading = ref(false)
  const refTaskId = ref<number | null>(null)
  const refComment = ref('')

  const disciplineId = (): string => route.params.id as string

  const refIsDeleteOpen = computed<boolean>(() => store.getActiveModal === DisciplineFilesModal.DeletePack)
  const refIsCertificateOpen = computed<boolean>(() => store.getActiveModal === DisciplineFilesModal.Certificate)

  /** The legacy title named the discipline, not the kid: "Generate certificate for: Math (K3)". */
  const refCertificateTitle = computed<string>(() => {
    const discipline = disciplineStore.getDiscipline
    const name = getDisciplineName(discipline?.code ?? '', listsStore.getLists?.discipline.code)
    return `Generate certificate for: ${name} (${discipline?.kid.grade ?? ''})`
  })

  const refTaskOptions = computed<NativeSelectOption[]>(() =>
    store.getTasks.map(task => ({
      id: task.id,
      value: task.id,
      text: getTaskOptionText(task),
      selected: task.id === refTaskId.value,
    })))

  const setTask = (option: NativeSelectOption): void => {
    refTaskId.value = option.value === null ? null : Number(option.value)
  }

  const setComment = (event: AppTextareaEvent): void => {
    refComment.value = event.value
  }

  const closeModal = (): void => {
    if (refIsLoading.value) return
    store.closeModal()
  }

  const finish = async (): Promise<void> => {
    store.closeModal()
    await useDisciplineFilesApi({ disciplineId: disciplineId() })
  }

  const deletePack = async (): Promise<void> => {
    const packId = store.getActivePackId
    if (!packId || refIsLoading.value) return

    await useDisciplineFilePackDeleteApi({
      disciplineId: disciplineId(),
      packId,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    await finish()
  }

  /** The PDF renders inside the request; a failure may still have created the pack, so the list is reread. */
  const generateCertificate = async (): Promise<void> => {
    if (refIsLoading.value) return

    await useDisciplineCertificateApi({
      disciplineId: disciplineId(),
      payload: {
        comment: refComment.value.trim() || null,
        ...(refTaskId.value && { task_id: refTaskId.value }),
      },
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    await finish()
  }

  watch(refIsCertificateOpen, (isOpen) => {
    if (!isOpen) return

    refTaskId.value = null
    refComment.value = ''
  })

  return {
    refIsDeleteOpen,
    refIsCertificateOpen,
    refIsLoading,
    refCertificateTitle,
    refTaskOptions,
    refComment,
    setTask,
    setComment,
    closeModal,
    deletePack,
    generateCertificate,
  }
}
